import { apiBaseUrl, clearSessionOrgId, setSessionOrgId } from "./config";

const ACCESS_TOKEN_KEY = "cimis.accessToken";

export type SubscriptionSummary = {
  status: string;
  planCode?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  trialConsumed?: boolean;
  compareQuotaMonthly?: number | null;
  compareUsed?: number;
  usageYm?: string | null;
  pointBalance?: number;
};

export type AuthSession = {
  accessToken: string;
  expiresIn: number;
  tokenType: string;
  userId: number;
  orgId: number;
  displayName: string;
  roles: string[];
  subscription: SubscriptionSummary;
};

export function getAccessToken(): string | null {
  const value = localStorage.getItem(ACCESS_TOKEN_KEY);
  return value && value.trim() ? value.trim() : null;
}

export function setAccessToken(token: string): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, token);
}

export function clearAccessToken(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
}

export function logout(): void {
  clearAccessToken();
  clearSessionOrgId();
}

async function postAuth(path: string, body: unknown): Promise<AuthSession> {
  const response = await fetch(`${apiBaseUrl()}${path}`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const message =
      typeof payload?.message === "string" ? payload.message : `请求失败 (${response.status})`;
    throw new Error(message);
  }
  const session = payload as AuthSession & { accessToken?: string };
  if (!session.accessToken) {
    throw new Error("登录响应缺少 accessToken");
  }
  setAccessToken(session.accessToken);
  setSessionOrgId(session.orgId);
  return session;
}

export function login(loginName: string, password: string): Promise<AuthSession> {
  return postAuth("/v1/auth/login", { loginName, password });
}

export function register(input: {
  orgName: string;
  loginName: string;
  password: string;
  displayName: string;
}): Promise<AuthSession> {
  return postAuth("/v1/auth/register", input);
}

/** @deprecated OIDC 主路径已停用，保留空实现避免旧引用崩溃 */
export type OidcPublicConfig = {
  enabled: boolean;
  mode?: string;
  hint?: string;
};

export async function fetchOidcConfig(): Promise<OidcPublicConfig> {
  return { enabled: false, mode: "password", hint: "请使用账号密码登录" };
}

export async function startLogin(): Promise<{ status: "disabled"; message: string; config: OidcPublicConfig }> {
  const config = await fetchOidcConfig();
  return { status: "disabled", message: "请使用账号密码登录", config };
}

/** @deprecated OIDC 回调已停用；旧路由仍引用此符号 */
export async function handleCallback(_params: {
  code?: string | null;
  state?: string | null;
  error?: string | null;
  error_description?: string | null;
}): Promise<void> {
  throw new Error("请使用账号密码登录");
}
